/**
 * 速度ランプ (シーンの中で再生速度を from → to へ連続的に変える) の正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.1-L。
 *
 * 同じ意味・同じ数式の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出し)
 * - video-editor リポジトリの src/schema/speedRamp.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/speed_ramp.py (AI 提案の正規化・合成フレーム・要約)
 *
 * ## 意味
 * - `fromSpeed` / `toSpeed`: シーンの先頭 / 末尾での再生速度 (0.25〜4)。
 * - `easing`: 速度の変わり方 (`linear` 既定 / `ease-in` / `ease-out` / `ease-in-out`)。
 *
 * `speed` (一定倍率) とは**排他**で、`speedRamp` があるシーンでは `speed` を無視する
 * (正規化で `speed` を落とす)。静止画クリップには付けない。
 *
 * ## 時間モデル
 * 表示時間 t (0〜D) に対する速度を v(t) = from + (to - from) * e(t / D) と定義する
 * (e は easing 関数で e(0)=0・e(1)=1)。素材尺 S = outSec - inSec をちょうど使い切るので
 *
 *   ∫₀^D v dt = S  ⇔  D * (from + (to - from) * E) = S      … E = ∫₀¹ e(p) dp
 *
 * が成り立ち、**表示尺 D = S / (from + (to - from) * E)** と閉じた形で求まる
 * (linear なら E = 1/2 なので D = S / ((from + to) / 2) = 平均速度で割ったもの)。
 * 数値積分を使わないので、3 実装が丸め誤差なしで一致する。
 *
 * 経過素材秒は s(t) = D * (from * p + (to - from) * F(p)) (p = t / D、F(p) = ∫₀^p e)。
 * s(D) = D * (from + (to - from) * E) = S なので、末尾はちょうど OUT 点に一致する。
 *
 * ## 既知の制限
 * 可変速の音声は Remotion では鳴らせないため、**ランプ中のシーン音声は鳴らさない**
 * (プレビュー・MP4 とも無音。要約にも「(音声なし)」と出す)。プレビューの Player では
 * フレームごとに素材フレームを差し替えるためコマ送り気味に見えることがある。
 */
import { z } from 'zod';

/** 速度の変わり方。CSS の同名イージングと同じ考え方 (2 次)。 */
export const SPEED_RAMP_EASINGS = ['linear', 'ease-in', 'ease-out', 'ease-in-out'] as const;
export type SpeedRampEasing = (typeof SPEED_RAMP_EASINGS)[number];

export const DEFAULT_SPEED_RAMP_EASING: SpeedRampEasing = 'linear';

/** 速度の範囲。UI の速度セレクタ (SpeedPicker) と同じ 0.25〜4。 */
export const SPEED_RAMP_MIN = 0.25;
export const SPEED_RAMP_MAX = 4;
/** from と to がこれ未満しか違わない指定は「ランプではない」(speed で書くべき) とみなす。 */
export const SPEED_RAMP_MIN_DELTA = 0.05;

/**
 * 保存形。**範囲も必須も縛らない** (壊れた JSON でプロジェクトごと開けなくならないよう、
 * クランプ・丸め・欠落の判定は normalizeSpeedRamp が行う。LoudnessSchema・DuckingSchema と
 * 同じ方針で、数値キーはすべて省略可にしてある。欠けた値を持つオブジェクトは
 * 読み出し側 (`itemSpeedRamp` / `rampDisplayDurationSec`) が「ランプなし」として扱う)。
 */
export const SpeedRampSchema = z.object({
  fromSpeed: z.number().optional(),
  toSpeed: z.number().optional(),
  easing: z.enum(SPEED_RAMP_EASINGS).optional(),
});
export type SpeedRamp = z.infer<typeof SpeedRampSchema>;

/** 正規化後の速度ランプ。数値キーは必ず埋まる (保存形の `SpeedRamp` は省略可)。 */
export interface ResolvedSpeedRamp {
  fromSpeed: number;
  toSpeed: number;
  easing?: SpeedRampEasing;
}

/** 数値キーが揃っているランプだけを返す (壊れた保存データは「ランプなし」に倒す)。 */
export function resolvedSpeedRamp(ramp: SpeedRamp | null | undefined): ResolvedSpeedRamp | undefined {
  if (!ramp) return undefined;
  const fromSpeed = finiteNumber(ramp.fromSpeed);
  const toSpeed = finiteNumber(ramp.toSpeed);
  if (fromSpeed === null || toSpeed === null) return undefined;
  const easing = resolveSpeedRampEasing(ramp);
  return easing === DEFAULT_SPEED_RAMP_EASING
    ? { fromSpeed, toSpeed }
    : { fromSpeed, toSpeed, easing };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * 小数 2 桁への half-up 丸め (backend の speed_ramp.py の `_round2` と同じ規則)。
 * **-0 は 0 に正規化する** (JS は -0 を返すが Python は 0.0 を返すため)。
 */
function round2(value: number): number {
  const rounded = Math.floor(value * 100 + 0.5) / 100;
  return rounded === 0 ? 0 : rounded;
}

/** 小数 4 桁への half-up 丸め (3 実装の同値テストで符号・桁を揃えるため)。 */
function round4(value: number): number {
  const rounded = Math.floor(value * 10000 + 0.5) / 10000;
  return rounded === 0 ? 0 : rounded;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
}

/** 省略時の既定 (linear) を埋めた easing。 */
export function resolveSpeedRampEasing(ramp: SpeedRamp | null | undefined): SpeedRampEasing {
  const easing = ramp?.easing;
  return easing && (SPEED_RAMP_EASINGS as readonly string[]).includes(easing)
    ? (easing as SpeedRampEasing)
    : DEFAULT_SPEED_RAMP_EASING;
}

/**
 * 外部入力 (AI の提案・古い保存データ・UI の数値) を保存形へ正規化する。
 * **保存値 = 実効値**にするため、範囲へクランプして 0.01 刻みに丸めてから
 * 「from と to がほぼ同じ」(= 一定速度) を undefined に倒す (その場合は speed を使わせる)。
 */
export function normalizeSpeedRamp(value: unknown): ResolvedSpeedRamp | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const rawFrom = finiteNumber(raw.fromSpeed);
  const rawTo = finiteNumber(raw.toSpeed);
  if (rawFrom === null || rawTo === null) return undefined;
  const fromSpeed = round2(clamp(rawFrom, SPEED_RAMP_MIN, SPEED_RAMP_MAX));
  const toSpeed = round2(clamp(rawTo, SPEED_RAMP_MIN, SPEED_RAMP_MAX));
  if (Math.abs(fromSpeed - toSpeed) < SPEED_RAMP_MIN_DELTA) return undefined;
  const easing = resolveSpeedRampEasing({ fromSpeed, toSpeed, easing: raw.easing as SpeedRampEasing });
  return easing === DEFAULT_SPEED_RAMP_EASING
    ? { fromSpeed, toSpeed }
    : { fromSpeed, toSpeed, easing };
}

/**
 * easing の 1 周期ぶんの平均 E = ∫₀¹ e(p) dp (解析解)。
 * - linear: e(p) = p → 1/2
 * - ease-in: e(p) = p² → 1/3
 * - ease-out: e(p) = 1 - (1-p)² → 2/3
 * - ease-in-out: 前半 2p² / 後半 1 - 2(1-p)² → 1/2 (対称なので linear と同じ)
 */
function easingMean(easing: SpeedRampEasing): number {
  if (easing === 'ease-in') return 1 / 3;
  if (easing === 'ease-out') return 2 / 3;
  return 1 / 2;
}

/** easing の不定積分 F(p) = ∫₀^p e(q) dq (0 ≤ p ≤ 1)。 */
function easingIntegral(easing: SpeedRampEasing, p: number): number {
  const x = clamp(p, 0, 1);
  if (easing === 'ease-in') return (x * x * x) / 3;
  if (easing === 'ease-out') return x * x - (x * x * x) / 3;
  if (easing === 'ease-in-out') {
    if (x <= 0.5) return (2 * x * x * x) / 3;
    const inv = 1 - x;
    return x - 0.5 + (2 * inv * inv * inv) / 3;
  }
  return (x * x) / 2;
}

/** easing 関数そのもの e(p) (0→1)。速度 v(t) を求めるときに使う。 */
export function speedRampEasingValue(easing: SpeedRampEasing, p: number): number {
  const x = clamp(p, 0, 1);
  if (easing === 'ease-in') return x * x;
  if (easing === 'ease-out') return 1 - (1 - x) * (1 - x);
  if (easing === 'ease-in-out') {
    if (x <= 0.5) return 2 * x * x;
    const inv = 1 - x;
    return 1 - 2 * inv * inv;
  }
  return x;
}

/**
 * 素材尺 S (= outSec - inSec) をランプで再生したときの**表示尺** (秒)。
 * S が 0 以下、または平均速度が 0 以下なら 0 を返す。
 */
export function rampDisplayDurationSec(sourceDurationSec: number, ramp: SpeedRamp): number {
  if (!(sourceDurationSec > 0)) return 0;
  const resolved = resolvedSpeedRamp(ramp);
  if (!resolved) return 0;
  const easing = resolveSpeedRampEasing(resolved);
  const mean = resolved.fromSpeed + (resolved.toSpeed - resolved.fromSpeed) * easingMean(easing);
  if (!(mean > 0)) return 0;
  return round4(sourceDurationSec / mean);
}

/**
 * 表示秒 t の時点で、素材を IN 点から何秒進めたか (0 〜 S)。
 * `t` は [0, D] にクランプするので、末尾では必ず S (= OUT 点) に一致する。
 */
export function rampSourceSecAt(displaySec: number, sourceDurationSec: number, ramp: SpeedRamp): number {
  const duration = rampDisplayDurationSec(sourceDurationSec, ramp);
  const resolved = resolvedSpeedRamp(ramp);
  if (!(duration > 0) || !resolved) return 0;
  const p = clamp(displaySec / duration, 0, 1);
  const easing = resolveSpeedRampEasing(resolved);
  const elapsed =
    duration
    * (resolved.fromSpeed * p
      + (resolved.toSpeed - resolved.fromSpeed) * easingIntegral(easing, p));
  return round4(clamp(elapsed, 0, sourceDurationSec));
}

/** 表示秒 t の時点での再生速度 v(t) (分割の近似で使う)。 */
export function rampSpeedAt(displaySec: number, sourceDurationSec: number, ramp: SpeedRamp): number {
  const resolved = resolvedSpeedRamp(ramp);
  if (!resolved) return 0;
  const duration = rampDisplayDurationSec(sourceDurationSec, resolved);
  if (!(duration > 0)) return resolved.fromSpeed;
  const p = clamp(displaySec / duration, 0, 1);
  const easing = resolveSpeedRampEasing(resolved);
  return round4(
    resolved.fromSpeed
    + (resolved.toSpeed - resolved.fromSpeed) * speedRampEasingValue(easing, p),
  );
}

/** ランプを表示秒 t で 2 つに分けたときの、前半 / 後半の近似ランプ (どちらも linear)。 */
export function splitSpeedRamp(
  ramp: SpeedRamp,
  sourceDurationSec: number,
  displaySec: number,
): { first: ResolvedSpeedRamp | undefined; second: ResolvedSpeedRamp | undefined } {
  const middle = rampSpeedAt(displaySec, sourceDurationSec, ramp);
  return {
    // easing は捨てる (分割後は「その区間で実際に使われた速度の始点 → 終点」の直線で近似する)
    first: normalizeSpeedRamp({ fromSpeed: ramp.fromSpeed, toSpeed: middle }),
    second: normalizeSpeedRamp({ fromSpeed: middle, toSpeed: ramp.toSpeed }),
  };
}

/** UI / 要約 / README に出す 1 行 (backend の describe_speed_ramp と同じ文言)。 */
export function speedRampLabel(ramp: SpeedRamp | null | undefined): string {
  const resolved = resolvedSpeedRamp(ramp);
  if (!resolved) return 'なし';
  const easing = resolveSpeedRampEasing(resolved);
  const note = easing === DEFAULT_SPEED_RAMP_EASING ? '音声なし' : `${easing}・音声なし`;
  return `${resolved.fromSpeed}→${resolved.toSpeed} (${note})`;
}
